import * as z from 'zod'
import { DUserListItem, UserListItem } from '../Users.js'
import { PaginatedResponse } from '../PaginatedResponse.js'
import { DeleteApiContract, GetApiContract, PatchApiContract, PostApiContract } from './ApiContract.js'
import { DUserAdminDetail, UserAdminDetail, UserMe } from '../Users.js'
import { userSettingsSchema } from '../UsersSettings.js'
import { ShallowDehydrateObject } from 'kysely'

export interface GetUserMeRequest extends GetApiContract {
    Response: UserMe
    ResponseJson: UserMe
}

export const USER_LIST_SORT_FIELDS = [
    'name',
    'email',
    'customerName',
    'userCategory',
    'disabled',
    'lastActive',
    'deviceCount',
] as const

export type UserListSortField = (typeof USER_LIST_SORT_FIELDS)[number]

export const getUserListQuerySchema = z.object({
    customerId: z.string().optional(),
    page: z.coerce.number(),
    limit: z.coerce.number().max(100),
    enabledDisabled: z.enum(['all', 'enabled', 'disabled']).optional(),
    userCategory: z.enum(['all', 'standard', 'trial', 'inhouse']).optional(),
    sortField: z.enum(USER_LIST_SORT_FIELDS).optional(),
    sortDirection: z.enum(['asc', 'desc']).optional(),
})

export type UserListQueryParameters = z.infer<typeof getUserListQuerySchema>

export interface GetUserListRequest extends GetApiContract {
    Response: PaginatedResponse<DUserListItem>
    ResponseJson: PaginatedResponse<UserListItem>
    QueryParameters: UserListQueryParameters
}

export interface GetUserByIdRequest extends GetApiContract {
    Response: DUserAdminDetail
    ResponseJson: UserAdminDetail
}

export interface GetUserMeEncryptionKeyRequest extends GetApiContract {
    Response: { encryptionKey: string }
    ResponseJson: { encryptionKey: string }
}

export const patchUserMeBodySchema = z.object({
    password: z
        .object({
            currentPassword: z.string(),
            newPassword: z.string(),
        })
        .optional(),
    settings: userSettingsSchema.optional(),
})

export interface PatchUserMeRequest extends PatchApiContract {
    Body: z.infer<typeof patchUserMeBodySchema>
    Response: UserMe
    ResponseJson: UserMe
}

export const patchUserBodySchema = z.object({
    customerId: z.string().optional().nullable(),
    disabled: z.boolean().optional(),
    password: z.string().optional(),
    userCategory: z.string().optional(),
})

export interface PatchUserRequest extends PatchApiContract {
    Body: z.infer<typeof patchUserBodySchema>
    Response: { message: string }
    ResponseJson: { message: string }
}

export const postUserBodySchema = z.object({
    email: z.email(),
    name: z.string(),
    password: z.string(),
    customerId: z.string().optional().nullable(),
    userCategory: z.string().optional(),
})

export interface PostUserRequest<
    R = {
        user: {
            userId: string | null
            username: string
            email: string
            name: string
            createdAt?: Date
        }
    },
> extends PostApiContract {
    Body: z.infer<typeof postUserBodySchema>
    Response: R
    ResponseJson: ShallowDehydrateObject<R>
}

export const postUserRequestResetPassword = z.object({
    email: z.string(),
})

export interface PostUserRequestResetPassword extends PostApiContract {
    Body: z.infer<typeof postUserRequestResetPassword>
}

export const postUserPasswordSchema = z.object({
    code: z.string(),
    email: z.string(),
    password: z.string(),
})

export interface PostUserResetPassword extends PostApiContract {
    Body: z.infer<typeof postUserPasswordSchema>
}

export interface DeleteUserRequest extends DeleteApiContract {
    Response: { message: string }
    ResponseJson: { message: string }
}
